import axios from 'axios';
import { getAdminApiBaseUrl } from '../config/adminRuntime';

interface RetriableRequest {
  _retry?: boolean;
}

interface RefreshResponse {
  accessToken?: string;
  refreshToken?: string;
}

let refreshPromise: Promise<string> | null = null;

function clearAdminSession(): void {
  localStorage.removeItem('adminToken');
  localStorage.removeItem('adminRefreshToken');
  localStorage.removeItem('adminUser');
}

async function refreshAdminAccessToken(): Promise<string> {
  if (refreshPromise) return refreshPromise;

  const refreshToken = localStorage.getItem('adminRefreshToken');
  if (!refreshToken) throw new Error('No refresh token available');

  refreshPromise = apiClient
    .post<RefreshResponse>('/admin/auth/refresh', { refreshToken })
    .then((response) => {
      const accessToken = response.data.accessToken;
      const nextRefreshToken = response.data.refreshToken;
      if (!accessToken || !nextRefreshToken) throw new Error('Invalid refresh response');

      localStorage.setItem('adminToken', accessToken);
      localStorage.setItem('adminRefreshToken', nextRefreshToken);
      return accessToken;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}

/**
 * Parses common API payloads from the portal API: `{ error }`, `{ message }`,
 * ProblemDetails, ASP.NET `{ errors }`, or plain response body strings.
 */
function isGatewayHtmlBody(text: string): boolean {
  const t = text.trim();
  return (
    /^<!DOCTYPE\s+html/i.test(t) ||
    /^<html[\s>]/i.test(t) ||
    /Request Entity Too Large/i.test(t) ||
    /nginx\//i.test(t)
  );
}

export function getErrorMessage(error: unknown, fallback = 'An unexpected error occurred.'): string {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    if (status === 413) {
      return 'This file is too large. Please upload a PDF or image up to 28 MB.';
    }

    const raw = error.response?.data;
    if (raw === undefined || raw === null) return error.message || fallback;

    if (typeof raw === 'string') {
      const t = raw.trim();
      if (!t || isGatewayHtmlBody(t)) {
        return status ? `Request failed (${status}).` : error.message || fallback;
      }
      return t;
    }

    if (typeof raw === 'object') {
      const d = raw as Record<string, unknown>;
      /** Application layer `ClientPortal.Application.Common.Result` JSON */
      if (typeof d.isSuccess === 'boolean' && d.isSuccess === false) {
        if (typeof d.error === 'string' && d.error.trim()) return d.error.trim();
        if (Array.isArray(d.errors)) {
          const parts = (d.errors as unknown[])
            .filter((x): x is string => typeof x === 'string' && !!x.trim())
            .map((x) => x.trim());
          if (parts.length > 0) return parts.slice(0, 5).join('; ');
        }
      }
      if (typeof d.error === 'string' && d.error.trim()) return d.error.trim();
      if (typeof d.message === 'string' && d.message.trim()) return d.message.trim();
      if (typeof d.title === 'string' && d.title.trim()) {
        const title = d.title.trim();
        if (typeof d.detail === 'string' && d.detail.trim()) return `${title}: ${d.detail.trim()}`;
        return title;
      }
      if (typeof d.detail === 'string' && d.detail.trim()) return d.detail.trim();

      if (d.errors && typeof d.errors === 'object') {
        const errs = d.errors as Record<string, unknown>;
        const parts: string[] = [];
        for (const v of Object.values(errs)) {
          if (Array.isArray(v)) {
            for (const x of v) {
              if (typeof x === 'string' && x.trim()) parts.push(x.trim());
            }
          } else if (typeof v === 'string' && v.trim()) {
            parts.push(v.trim());
          }
        }
        if (parts.length > 0) return parts.slice(0, 5).join(' ');
      }
    }

    return error.message || fallback;
  }

  if (error instanceof Error && error.message.trim()) return error.message;
  return fallback;
}

const apiClient = axios.create({
  headers: {
    'Content-Type': 'application/json',
  },
});

apiClient.interceptors.request.use(
  (config) => {
    config.baseURL = getAdminApiBaseUrl();
    const token = localStorage.getItem('adminToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 403) {
      try {
        const raw = error.response?.data;
        const needMfa =
          typeof raw === 'object' &&
          raw !== null &&
          (raw as { adminMfaRequired?: boolean }).adminMfaRequired === true;
        if (needMfa && !window.location.pathname.startsWith('/settings')) {
          window.location.href = '/settings';
        }
      } catch {
        /* ignore malformed 403 payloads */
      }
    }
    if (error.response?.status === 401) {
      const requestUrl = String(error.config?.url || '');
      if (requestUrl.includes('/admin/auth/invitation') || requestUrl.includes('/admin/auth/accept-invitation')) {
        return Promise.reject(error);
      }

      const originalRequest = error.config as (typeof error.config & RetriableRequest) | undefined;
      const isRefreshRequest = requestUrl.includes('/admin/auth/refresh');
      if (!isRefreshRequest && originalRequest && !originalRequest._retry) {
        originalRequest._retry = true;
        try {
          const accessToken = await refreshAdminAccessToken();
          originalRequest.headers = originalRequest.headers ?? {};
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return apiClient(originalRequest);
        } catch {
          clearAdminSession();
          window.location.href = '/login';
          return Promise.reject(error);
        }
      }

      clearAdminSession();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default apiClient;
